import { Button, radius } from '@alison/ui';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { getRouteApi, useNavigate } from '@tanstack/react-router';
import { type ReactNode, useEffect, useState } from 'react';
import {
  type AdProvider,
  useAdNetworkStatus,
  useIntegrationProducts,
} from '../../lib/adNetwork.hooks.js';
import { linkSocial } from '../../lib/authClient.js';
import { ConnectDrawer } from './ConnectDrawer.js';
import { ConnectionCard } from './ConnectionCard.js';
import { NetworkCard } from './NetworkCard.js';
import {
  authorizationErrorMessage,
  matchesProviderSearch,
  OAUTH_PROVIDER,
  SUPPORTED_PROVIDERS,
} from './networks.js';

const route = getRouteApi('/settings/integrations/');

function SectionLabel({ children }: { children: ReactNode }): ReactNode {
  return (
    <Typography variant="overline" sx={{ color: 'text.secondary', fontSize: 13, fontWeight: 700 }}>
      {children}
    </Typography>
  );
}

export function IntegrationsPage(): ReactNode {
  const search = route.useSearch();
  const navigate = useNavigate();
  const products = useIntegrationProducts();

  const [productId, setProductId] = useState<number | undefined>(search.productId);
  const [drawerProvider, setDrawerProvider] = useState<AdProvider | null>(
    search.connected === '1' && search.provider !== undefined ? search.provider : null,
  );
  const [linkPending, setLinkPending] = useState<AdProvider | null>(null);
  const [linkError, setLinkError] = useState<string | null>(
    search.error === undefined ? null : authorizationErrorMessage(search.error),
  );
  const [providerSearch, setProviderSearch] = useState('');

  const metaStatus = useAdNetworkStatus('meta', productId);
  const linkedinStatus = useAdNetworkStatus('linkedin', productId);
  const googleStatus = useAdNetworkStatus('google', productId);
  const tiktokStatus = useAdNetworkStatus('tiktok', productId);
  const statuses = {
    meta: metaStatus,
    linkedin: linkedinStatus,
    google: googleStatus,
    tiktok: tiktokStatus,
  };
  const drawerStatus = drawerProvider === null ? undefined : statuses[drawerProvider];

  useEffect(() => {
    const onlyProduct = products.data?.length === 1 ? products.data[0] : undefined;
    if (onlyProduct !== undefined && productId !== onlyProduct.id) {
      setDrawerProvider(null);
      setProductId(onlyProduct.id);
    }
  }, [productId, products.data]);

  useEffect(() => {
    const finished = search.connected === '1' || search.error !== undefined;
    if (!finished) return;
    if (search.productId !== undefined) setProductId(search.productId);
    if (search.connected === '1' && search.provider !== undefined) {
      setDrawerProvider(search.provider);
    }
    if (search.error !== undefined) setLinkError(authorizationErrorMessage(search.error));
    void navigate({ to: '/settings/integrations', search: {}, replace: true });
  }, [search.connected, search.error, search.productId, search.provider, navigate]);

  async function startLink(provider: AdProvider): Promise<void> {
    if (productId === undefined) return;
    setLinkError(null);
    setLinkPending(provider);
    const baseURL = `${window.location.origin}/settings/integrations`;
    const flow = `provider=${provider}&productId=${productId}`;
    const callbackURL = `${baseURL}?connected=1&${flow}`;
    const errorCallbackURL = `${baseURL}?${flow}`;
    const result = await linkSocial({
      provider: OAUTH_PROVIDER[provider],
      callbackURL,
      errorCallbackURL,
    });
    if (result.error) {
      setLinkPending(null);
      setLinkError(result.error.message ?? 'Could not start authorization');
    } else if (result.data?.url) {
      window.location.assign(result.data.url);
    } else {
      setLinkPending(null);
      setLinkError('Authorization did not return a redirect');
    }
  }

  if (products.isPending) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (products.isError) {
    return (
      <Alert
        severity="error"
        action={
          <Button size="small" onClick={() => void products.refetch()}>
            Retry
          </Button>
        }
      >
        Could not load products.
      </Alert>
    );
  }

  if (products.data.length === 0) {
    return (
      <Typography color="text.secondary" data-testid="integrations-empty">
        Integrations aren't available for your current brand scope.
      </Typography>
    );
  }

  const providers = SUPPORTED_PROVIDERS;
  const connected = providers.filter(
    (provider) => statuses[provider].data && statuses[provider].data.state !== 'not_connected',
  );
  const availableProviders = providers.filter(
    (provider) => statuses[provider].data?.state === 'not_connected',
  );
  const visibleAvailable = availableProviders.filter((provider) =>
    matchesProviderSearch(provider, providerSearch),
  );
  const statusesLoading =
    productId !== undefined && providers.some((provider) => statuses[provider].isPending);
  const statusesErrored = providers.some((provider) => statuses[provider].isError);

  return (
    <Stack sx={{ gap: 6 }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        sx={{ gap: 3, justifyContent: 'space-between', alignItems: { md: 'center' } }}
      >
        <Stack sx={{ gap: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Connected platforms
          </Typography>
          <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>
            Control what Alison pulls from — and ships back to — across your ad networks.
          </Typography>
        </Stack>
        {products.data.length > 1 && (
          <TextField
            select
            size="small"
            label="Product"
            value={productId ?? ''}
            onChange={(event) => {
              setDrawerProvider(null);
              setProductId(Number(event.target.value));
            }}
            sx={{ minWidth: 280, '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
            slotProps={{ htmlInput: { 'data-testid': 'product-select' } }}
          >
            {products.data.map((product) => (
              <MenuItem
                key={product.id}
                value={product.id}
                data-testid={`product-option-${product.id}`}
              >
                {product.name} - {product.clientName}
              </MenuItem>
            ))}
          </TextField>
        )}
      </Stack>

      {linkError && <Alert severity="error">{linkError}</Alert>}

      {productId === undefined ? (
        <Typography color="text.secondary">Select a product to manage its ad networks.</Typography>
      ) : statusesLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <>
          {statusesErrored && (
            <Alert
              severity="error"
              action={
                <Button
                  size="small"
                  onClick={() => providers.forEach((provider) => void statuses[provider].refetch())}
                >
                  Retry
                </Button>
              }
            >
              Service unavailable, try again.
            </Alert>
          )}

          <Stack sx={{ gap: 3 }}>
            <SectionLabel>Your connections</SectionLabel>
            {connected.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {statusesErrored
                  ? 'Connection status unavailable.'
                  : 'Nothing connected for this product yet.'}
              </Typography>
            ) : (
              connected.map((provider) => {
                const status = statuses[provider].data;
                if (!status) return null;
                return (
                  <ConnectionCard
                    key={provider}
                    provider={provider}
                    status={status}
                    linkPending={linkPending === provider}
                    onAddAccounts={() => void startLink(provider)}
                    onManage={() => setDrawerProvider(provider)}
                    onReconnect={() => void startLink(provider)}
                  />
                );
              })
            )}
          </Stack>

          {availableProviders.length > 0 && (
            <Stack sx={{ gap: 3 }}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                sx={{ gap: 3, alignItems: { sm: 'center' }, justifyContent: 'space-between' }}
              >
                <SectionLabel>Add a network</SectionLabel>
                <TextField
                  size="small"
                  placeholder="Search networks…"
                  value={providerSearch}
                  onChange={(event) => setProviderSearch(event.target.value)}
                  slotProps={{ htmlInput: { 'data-testid': 'network-search' } }}
                  sx={{
                    width: { xs: '100%', sm: 260 },
                    '& .MuiOutlinedInput-root': { borderRadius: 1 },
                  }}
                />
              </Stack>
              {visibleAvailable.length === 0 ? (
                <Box
                  sx={{
                    py: 5,
                    border: '1px dashed',
                    borderColor: 'divider',
                    borderRadius: `${radius.xl}px`,
                    textAlign: 'center',
                    color: 'text.secondary',
                    fontSize: 13,
                  }}
                >
                  No networks match your search.
                </Box>
              ) : (
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(238px, 1fr))',
                    gap: 2,
                  }}
                >
                  {visibleAvailable.map((provider) => (
                    <NetworkCard
                      key={provider}
                      provider={provider}
                      linkPending={linkPending === provider}
                      onConnect={() => void startLink(provider)}
                    />
                  ))}
                </Box>
              )}
            </Stack>
          )}
        </>
      )}

      {drawerProvider !== null && productId !== undefined && (
        <ConnectDrawer
          key={drawerProvider}
          open
          onClose={() => setDrawerProvider(null)}
          provider={drawerProvider}
          productId={productId}
          status={drawerStatus?.data}
          statusPending={drawerStatus?.isPending ?? false}
          statusError={drawerStatus?.isError ?? false}
        />
      )}
    </Stack>
  );
}
